import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { Plus, Trash2, Search, Package, DollarSign, Copy, Save, X } from 'lucide-react';

const DEFAULT_SIZES = { S: 0, M: 0, L: 0, XL: 0, '2XL': 0, '3XL': 0 };

const ProductEditor = ({ proposal, onSave, onCancel }) => {
  const [items, setItems] = useState(proposal.items || []);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pricingPreview, setPricingPreview] = useState(null);
  const [loadingPricing, setLoadingPricing] = useState(false);

  // Search products from SanMar (multi-result) with S&S fallback.
  const searchProducts = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      // SanMar multi-result first — returns up to 10 matches ranked by
      // exact → prefix → substring. Works for "PC", "PC54", "Sport-Tek", etc.
      const sanmarResponse = await axios.get(
        `/sanmar/products/search-multi/${encodeURIComponent(query)}`
      );
      if (Array.isArray(sanmarResponse.data) && sanmarResponse.data.length > 0) {
        setSearchResults(
          sanmarResponse.data.map((r) => ({ ...r, source: r.supplier || 'SanMar' }))
        );
        return;
      }
    } catch (e) {
      // SanMar lookup failed; fall through to S&S
    }

    try {
      const response = await axios.get(`/products/search/all/${encodeURIComponent(query)}`);
      const results = Array.isArray(response.data) ? response.data : [response.data];
      setSearchResults(results.map((r) => ({ ...r, source: r.supplier || 'S&S' })));
    } catch (error) {
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // Debounced search
  useEffect(() => {
    const debounce = setTimeout(() => {
      searchProducts(searchQuery);
    }, 400);
    return () => clearTimeout(debounce);
  }, [searchQuery]);

  // Calculate pricing when items change
  useEffect(() => {
    calculatePricing();
  }, [items]);

  const calculatePricing = async () => {
    if (items.length === 0) {
      setPricingPreview(null);
      return;
    }

    setLoadingPricing(true);
    try {
      const totalQuantity = items.reduce((sum, item) => sum + (item.quantity || 0), 0);
      
      const response = await axios.post('/pricing/calculate', {
        quantity: totalQuantity,
        print_details: proposal.print_details,
        items: items.map(item => ({
          brand: item.brand || item.brandName,
          style: item.style || item.styleID,
          style_number: item.style_number || item.styleID,
          color: item.color || item.selectedColor,
          quantity: item.quantity,
          sizes: item.sizes || DEFAULT_SIZES,
          garment_cost: item.garment_cost || item.basePrice || 0,
          print_method: proposal.print_details?.method || 'screen_print',
          print_colors: Object.values(proposal.print_details?.colors_per_location || {})[0] || 1,
          print_locations: proposal.print_details?.locations || ['front'],
          location_count: proposal.print_details?.locations?.length || 1
        }))
      });
      
      setPricingPreview(response.data);
    } catch (error) {
      console.error('Pricing calculation error:', error);
    } finally {
      setLoadingPricing(false);
    }
  };

  // Add product to items
  const addProduct = (product) => {
    const newItem = {
      id: Date.now(),
      brand: product.brandName || product.brand,
      brandName: product.brandName || product.brand,
      style: product.styleID || product.style,
      styleID: product.styleID || product.style,
      style_number: product.styleID || product.style_number,
      styleName: product.styleName || product.description,
      color: product.colors?.[0]?.colorName || 'Black',
      selectedColor: product.colors?.[0]?.colorName || 'Black',
      colors: product.colors || [],
      quantity: 24,
      sizes: { ...DEFAULT_SIZES },
      garment_cost: product.basePrice || product.customerPrice || 0,
      basePrice: product.basePrice || 0,
      customerPrice: product.customerPrice || product.basePrice || 0,
      print_method: proposal.print_details?.method || 'screen_print',
      print_colors: Object.values(proposal.print_details?.colors_per_location || {})[0] || 1,
      print_locations: proposal.print_details?.locations || ['front'],
      location_count: proposal.print_details?.locations?.length || 1,
      source: product.source || 'Manual'
    };
    
    setItems([...items, newItem]);
    setSearchQuery('');
    setSearchResults([]);
    toast.success(`Added ${newItem.brand} ${newItem.style}`);
  };

  // Add a Customer-Provided Product (CPP) line: customer supplies the
  // garment, we only decorate. No SanMar/S&S lookup — admin fills in the
  // description, sizes, and any base cost manually.
  const addCPPProduct = () => {
    const newItem = {
      id: Date.now(),
      brand: 'Customer Provided',
      brandName: 'Customer Provided',
      style: 'CPP',
      styleID: 'CPP',
      style_number: 'CPP',
      styleName: 'Customer Provided Product',
      color: 'Various',
      selectedColor: 'Various',
      colors: [],
      quantity: 0,
      sizes: { ...DEFAULT_SIZES },
      garment_cost: 0,
      basePrice: 0,
      customerPrice: 0,
      markup_price: 0,
      is_cpp: true,
      print_method: proposal.print_details?.method || 'screen_print',
      print_colors: Object.values(proposal.print_details?.colors_per_location || {})[0] || 1,
      print_locations: proposal.print_details?.locations || ['front'],
      location_count: proposal.print_details?.locations?.length || 1,
      source: 'CPP',
    };
    setItems([...items, newItem]);
    toast.success('Customer-Provided Product added — decoration only. Update brand/style/sizes below.');
  };

  // Update item
  const updateItem = (itemId, field, value) => {
    setItems(items.map(item => {
      if (item.id !== itemId) return item;
      
      if (field === 'sizes') {
        const newSizes = { ...item.sizes, ...value };
        const newQuantity = Object.values(newSizes).reduce((sum, v) => sum + (parseInt(v) || 0), 0);
        return { ...item, sizes: newSizes, quantity: newQuantity };
      }
      
      if (field === 'color' || field === 'selectedColor') {
        return { ...item, color: value, selectedColor: value };
      }
      
      return { ...item, [field]: value };
    }));
  };

  // Remove item
  const removeItem = (itemId) => {
    setItems(items.filter(item => item.id !== itemId));
    toast.success('Product removed');
  };

  // Duplicate item
  const duplicateItem = (item) => {
    const newItem = {
      ...item,
      id: Date.now(),
      color: item.color,
      selectedColor: item.selectedColor
    };
    setItems([...items, newItem]);
    toast.success('Product duplicated');
  };

  // Save changes
  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(items, pricingPreview);
      toast.success('Products updated successfully');
    } catch (error) {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ background: 'white', borderRadius: '12px', padding: '24px', border: '1px solid #e2e8f0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3 style={{ fontSize: '18px', fontWeight: '600', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Package size={20} /> Edit Products
        </h3>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button variant="outline" onClick={onCancel}>
            <X size={16} className="mr-2" /> Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            <Save size={16} className="mr-2" /> {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Product Search */}
      <div style={{ marginBottom: '24px', background: '#f7fafc', padding: '16px', borderRadius: '8px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', gap: 12, flexWrap: 'wrap' }}>
          <label style={{ fontSize: '14px', fontWeight: '500' }}>
            Add Product (Search by style number)
          </label>
          {/* Customer-Provided Product button — for orders where the
              customer brings their own garment and we only decorate.
              No catalog lookup needed; admin fills in details after add. */}
          <Button
            type="button"
            onClick={addCPPProduct}
            data-testid="add-cpp-btn"
            variant="outline"
            size="sm"
            style={{
              borderColor: '#f59e0b',
              color: '#92400e',
              background: '#fef3c7',
              fontWeight: 600,
            }}
          >
            <Plus size={14} className="mr-1" />
            Customer Provided Product (CPP)
          </Button>
        </div>
        <div style={{ position: 'relative' }}>
          <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#a0aec0' }} />
          <Input
            placeholder="Search style... (e.g., PC61, G500, ST550)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ paddingLeft: '40px' }}
          />
        </div>
        
        {searchLoading && <p style={{ fontSize: '12px', color: '#718096', marginTop: '8px' }}>Searching...</p>}
        
        {searchResults.length > 0 && (
          <div style={{ marginTop: '12px', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
            {searchResults.map((product, idx) => (
              <div 
                key={idx}
                style={{ 
                  padding: '12px', 
                  borderBottom: idx < searchResults.length - 1 ? '1px solid #e2e8f0' : 'none',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  background: 'white'
                }}
                onClick={() => addProduct(product)}
              >
                <div>
                  <p style={{ fontWeight: '600', fontSize: '14px' }}>
                    {product.brandName} {product.styleID}
                    <span style={{ 
                      marginLeft: '8px', 
                      fontSize: '11px', 
                      padding: '2px 6px', 
                      background: product.source === 'SanMar' ? '#8b5cf6' : '#3b82f6',
                      color: 'white',
                      borderRadius: '4px'
                    }}>
                      {product.source}
                    </span>
                  </p>
                  <p style={{ fontSize: '12px', color: '#718096' }}>{product.styleName || product.description}</p>
                  <p style={{ fontSize: '12px', color: '#22c55e', fontWeight: '500' }}>
                    ${(product.customerPrice || product.basePrice || 0).toFixed(2)} per garment
                  </p>
                </div>
                <Button size="sm" variant="outline">
                  <Plus size={14} className="mr-1" /> Add
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Current Products */}
      <div style={{ marginBottom: '24px' }}>
        <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>
          Products in Proposal ({items.length})
        </h4>
        
        {items.length === 0 ? (
          <p style={{ color: '#718096', fontSize: '14px', textAlign: 'center', padding: '32px' }}>
            No products added. Search above to add products.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {items.map((item, idx) => (
              <div 
                key={item.id || idx}
                style={{ 
                  border: '1px solid #e2e8f0', 
                  borderRadius: '8px', 
                  padding: '16px',
                  background: '#fafafa'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                  <div>
                    <h5 style={{ fontWeight: '600', fontSize: '15px', marginBottom: '4px' }}>
                      {item.brand || item.brandName} - {item.style || item.styleID}
                    </h5>
                    <p style={{ fontSize: '12px', color: '#718096' }}>{item.styleName}</p>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Button size="sm" variant="ghost" onClick={() => duplicateItem(item)} title="Duplicate">
                      <Copy size={14} />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => removeItem(item.id)} title="Remove" style={{ color: '#ef4444' }}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '12px' }}>
                  {/* Color Selection */}
                  <div>
                    <label style={{ fontSize: '12px', color: '#718096', marginBottom: '4px', display: 'block' }}>Color</label>
                    {item.colors && item.colors.length > 0 ? (
                      <Select value={item.color || item.selectedColor} onValueChange={(v) => updateItem(item.id, 'color', v)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {item.colors.map((c, cidx) => (
                            <SelectItem key={cidx} value={c.colorName || c}>{c.colorName || c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input 
                        value={item.color || item.selectedColor || ''} 
                        onChange={(e) => updateItem(item.id, 'color', e.target.value)}
                      />
                    )}
                  </div>

                  {/* Garment Cost */}
                  <div>
                    <label style={{ fontSize: '12px', color: '#718096', marginBottom: '4px', display: 'block' }}>Garment Cost</label>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <span style={{ marginRight: '4px' }}>$</span>
                      <Input 
                        type="number"
                        step="0.01"
                        value={item.garment_cost || item.basePrice || 0}
                        onChange={(e) => updateItem(item.id, 'garment_cost', parseFloat(e.target.value) || 0)}
                        style={{ width: '100px' }}
                      />
                    </div>
                  </div>

                  {/* Quantity */}
                  <div>
                    <label style={{ fontSize: '12px', color: '#718096', marginBottom: '4px', display: 'block' }}>Total Qty</label>
                    <Input 
                      type="number"
                      value={item.quantity || 0}
                      onChange={(e) => updateItem(item.id, 'quantity', parseInt(e.target.value) || 0)}
                      style={{ width: '80px' }}
                    />
                  </div>
                </div>

                {/* Sizes */}
                <div>
                  <label style={{ fontSize: '12px', color: '#718096', marginBottom: '8px', display: 'block' }}>Sizes</label>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {Object.entries(item.sizes || DEFAULT_SIZES).map(([size, qty]) => (
                      <div key={size} style={{ textAlign: 'center' }}>
                        <label style={{ fontSize: '11px', color: '#718096', display: 'block' }}>{size}</label>
                        <Input
                          type="number"
                          min="0"
                          value={qty || 0}
                          onChange={(e) => updateItem(item.id, 'sizes', { [size]: parseInt(e.target.value) || 0 })}
                          style={{ width: '50px', textAlign: 'center', padding: '4px' }}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Item Pricing */}
                <div style={{ marginTop: '12px', padding: '12px', background: '#e8f5e9', borderRadius: '6px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                    <span>Garment: ${((item.garment_cost || 0) * (item.quantity || 0)).toFixed(2)}</span>
                    <span>Print: ${((item.print_cost || 1.5) * (item.quantity || 0)).toFixed(2)}</span>
                    <span style={{ fontWeight: '600' }}>
                      Item Total: ${(((item.garment_cost || 0) + (item.print_cost || 1.5)) * (item.quantity || 0)).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pricing Summary */}
      {pricingPreview && (
        <div style={{ background: '#1a1a2e', color: 'white', padding: '20px', borderRadius: '8px' }}>
          <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <DollarSign size={18} /> Pricing Summary
            {loadingPricing && <span style={{ fontSize: '12px', color: '#a0aec0' }}>(calculating...)</span>}
          </h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '16px' }}>
            <div>
              <p style={{ fontSize: '12px', color: '#a0aec0' }}>Garments</p>
              <p style={{ fontSize: '18px', fontWeight: '600' }}>${(pricingPreview.shirt_total || 0).toFixed(2)}</p>
            </div>
            <div>
              <p style={{ fontSize: '12px', color: '#a0aec0' }}>Decoration</p>
              <p style={{ fontSize: '18px', fontWeight: '600' }}>${(pricingPreview.print_total || 0).toFixed(2)}</p>
            </div>
            <div>
              <p style={{ fontSize: '12px', color: '#a0aec0' }}>Setup Fees</p>
              <p style={{ fontSize: '18px', fontWeight: '600' }}>${(pricingPreview.setup_fees || 0).toFixed(2)}</p>
            </div>
            <div style={{ borderLeft: '2px solid #667eea', paddingLeft: '16px' }}>
              <p style={{ fontSize: '12px', color: '#a0aec0' }}>Grand Total</p>
              <p style={{ fontSize: '24px', fontWeight: '700', color: '#22c55e' }}>${(pricingPreview.total_amount || 0).toFixed(2)}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductEditor;
